/**
 * Persist live-HTML screen routes on the Drawing record in IndexedDB.
 * Merges routes rebuilt from host comment.screen rows (package redirect guide).
 */

import { mergeHostCommentRoutes } from './prototypeDrawingStorage'

const IDB_NAME = 'canvas-demo'

const openDrawingsDb = () =>
    new Promise((resolve, reject) => {
        const req = indexedDB.open(IDB_NAME, 1)
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
    })

const readDrawingRecord = async (drawingId) => {
    if (!drawingId) return null
    const db = await openDrawingsDb()
    try {
        return await new Promise((resolve, reject) => {
            const tx = db.transaction('drawings', 'readonly')
            const req = tx.objectStore('drawings').get(drawingId)
            req.onsuccess = () => resolve(req.result || null)
            req.onerror = () => reject(req.error)
        })
    } finally {
        db.close()
    }
}

const putDrawingRecord = async (drawing) => {
    if (!drawing?.id) return null
    const db = await openDrawingsDb()
    try {
        return await new Promise((resolve, reject) => {
            const tx = db.transaction('drawings', 'readwrite')
            const req = tx.objectStore('drawings').put(drawing)
            tx.oncomplete = () => resolve(drawing)
            tx.onerror = () => reject(tx.error || req.error)
        })
    } finally {
        db.close()
    }
}

/**
 * Screen-id fingerprints hash the live HTML's *visible* text, which depends on
 * the iframe's rendered width (responsive breakpoints). The package scales the
 * iframe to fit whatever `htmlLayoutViewport` says, so pinning it to the
 * prototype's own design width makes every session — original author, hard
 * reload, incognito, another teammate's machine — fingerprint screens
 * identically. Without this, redirect only matches while the *same* browser
 * window that authored the comment is still open.
 */
const layoutViewportFromDesignWidth = (designWidth) => {
    const width = Number(designWidth) > 0 ? Math.round(Number(designWidth)) : 1440
    return { width, height: Math.round(width * 0.75) }
}

/**
 * Write htmlScreenRoutes onto the Drawing before the editor hydrates.
 * Pass editorComments so routes can be rebuilt from comment.screen when the
 * Drawing never stored routes (fresh device / cleared IDB).
 */
export const restoreHtmlScreenRoutes = async (
    drawingId,
    _prototypeId,
    _versionId,
    editorComments = [],
    designWidth
) => {
    if (!drawingId) return null
    const drawing = await readDrawingRecord(drawingId)
    if (!drawing) return null

    const existing = Array.isArray(drawing.htmlScreenRoutes)
        ? drawing.htmlScreenRoutes
        : []
    const routes = mergeHostCommentRoutes(existing, editorComments)

    const blobId = drawing.backgroundBlobId || drawing.htmlScreenRoutesBlobId || ''
    drawing.htmlScreenRoutes = routes.length ? routes : existing
    drawing.htmlScreenRoutesBlobId = blobId
    drawing.htmlLayoutViewport = layoutViewportFromDesignWidth(designWidth)
    drawing.updatedAt = new Date().toISOString()
    await putDrawingRecord(drawing)
    return drawing
}
